import { useQuery } from "@tanstack/react-query";
import { apiClient, call } from "../../shared/api/client";
import type { FeedbackAvailability, FeedbackReportResult, FeedbackReportSubmission } from "../../shared/api/types";

/** GET /feedback-reports/availability, only while Features__PinFeedback is on (404 feature-disabled otherwise). */
export function useFeedbackAvailability(enabled: boolean) {
  return useQuery({
    queryKey: ["feedback", "availability"],
    queryFn: (): Promise<FeedbackAvailability> => call(apiClient.GET("/feedback-reports/availability")),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * The multipart body, built by hand with a boundary derived from the Idempotency-Key: the server hashes the raw body to
 * recognise a retry, and a browser FormData picks a new random boundary every time, which would turn an honest retry
 * into 422 key_reused. Same report + same screenshot + same key = byte-identical body. Bytes, not a Blob, so every
 * fetch implementation sends it as is.
 */
export async function feedbackMultipart(reportJson: string, screenshot: Blob | null, idempotencyKey: string): Promise<{ body: Uint8Array; contentType: string }> {
  const boundary = `giraffe-feedback-${idempotencyKey.replace(/[^A-Za-z0-9-]/g, "")}`;
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [
    encoder.encode(`--${boundary}\r\nContent-Disposition: form-data; name="report"; filename="report.json"\r\nContent-Type: application/json\r\n\r\n`),
    encoder.encode(reportJson),
    encoder.encode("\r\n"),
  ];
  if (screenshot) {
    chunks.push(
      encoder.encode(`--${boundary}\r\nContent-Disposition: form-data; name="screenshot"; filename="screenshot.png"\r\nContent-Type: image/png\r\n\r\n`),
      new Uint8Array(await screenshot.arrayBuffer()),
      encoder.encode("\r\n"),
    );
  }
  chunks.push(encoder.encode(`--${boundary}--\r\n`));
  const body = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    body.set(chunk, offset);
    offset += chunk.length;
  }
  return { body, contentType: `multipart/form-data; boundary=${boundary}` };
}

/**
 * POST /feedback-reports: the `report` JSON part and, when captured, the `screenshot` PNG part. One Idempotency-Key per
 * report; a retry after a timeout sends the same `reportJson`, screenshot and key and never files twice.
 */
export async function submitFeedbackReport(reportJson: string, screenshot: Blob | null, idempotencyKey: string): Promise<FeedbackReportResult> {
  const { body, contentType } = await feedbackMultipart(reportJson, screenshot, idempotencyKey);
  return call(
    apiClient.POST("/feedback-reports", {
      params: { header: { "Idempotency-Key": idempotencyKey } },
      headers: { "Content-Type": contentType },
      body: {} as never,
      bodySerializer: () => body,
    }),
  );
}

export function serializeReport(report: FeedbackReportSubmission): string {
  return JSON.stringify(report);
}
